import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Grid,
  Typography,
  Divider,
  IconButton,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import MainContainer from 'theme/MainConatiner';
import CustomButton from 'components/Fileds/CustomButton';
import CustomSelect from 'components/Fileds/CustomSelect';
import CustomTextArea from 'components/Fileds/CustomTextArea';
import {
  useGetTicketDetailQuery,
  useReplyTicketMutation,
  useUpdateTicketStatusMutation,
  useUploadAttachmentsMutation,
} from './services';
import { CATEGORY_OPTIONS, PRIORITY_OPTIONS } from './CreateTicketScreen';
import { getStatusBadge, getPriorityBadge, formatFullDate, parseAttachments, getFileUrl, getFileName } from './utils';
import './style.css';

const TicketDetailScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [replyMessage, setReplyMessage] = useState('');
  const [replyFiles, setReplyFiles] = useState([]); // Staged reply attachments

  const { data: ticketData, isLoading, isError, refetch } = useGetTicketDetailQuery(id);
  const [replyTicket, { isLoading: isReplying }] = useReplyTicketMutation();
  const [updateTicketStatus, { isLoading: isUpdatingStatus }] = useUpdateTicketStatusMutation();
  const [uploadAttachments, { isLoading: isUploading }] = useUploadAttachmentsMutation();

  const ticket = ticketData?.data ?? ticketData ?? null;
  const messages = ticket?.messages ?? [];

  // Safely parse attachments for main ticket
  const ticketAttachments = parseAttachments(ticket?.attachments);

  // Filter messages to show all real conversation messages (excluding empty system internal notes)
  const conversationMessages = messages.filter(
    (msg) => !msg.is_internal_note && ((msg.message && msg.message.trim() !== '') || parseAttachments(msg.attachments).length > 0)
  );

  // Find the last conversation message
  const lastMessage = conversationMessages.length > 0
    ? conversationMessages[conversationMessages.length - 1]
    : null;

  const isLastFromAdmin = lastMessage
    ? (lastMessage.sender_type === 'admin' || lastMessage.sender_type === 'super_admin' || (lastMessage.sender?.role && lastMessage.sender.role.toLowerCase().includes('admin')))
    : (ticket?.last_replied_by === 'admin');

  const handleReplyFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setReplyFiles((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const handleRemoveReplyFile = (idxToRemove) => {
    setReplyFiles((prev) => prev.filter((_, idx) => idx !== idxToRemove));
  };

  const handleSendReply = async () => {
    if ((!replyMessage.trim() && replyFiles.length === 0) || isReplying || isUploading) return;

    try {
      let uploadedReplyAttachments = [];

      // Upload reply files at send time if any selected
      if (replyFiles.length > 0) {
        const formData = new FormData();
        replyFiles.forEach((file) => formData.append('files', file));

        const uploadRes = await uploadAttachments(formData).unwrap();
        const uploadedData = uploadRes?.data || [];

        if (Array.isArray(uploadedData)) {
          uploadedReplyAttachments = uploadedData.map((att) => ({
            fileName: att.fileName || att.filename || 'file',
            fileUrl: att.fileUrl || att.url || att.filename,
            fileType: att.fileType || att.type || 'application/octet-stream',
            fileSize: att.fileSize || att.size || 0,
          }));
        } else if (uploadedData) {
          uploadedReplyAttachments = [{
            fileName: uploadedData.fileName || uploadedData.filename || 'file',
            fileUrl: uploadedData.fileUrl || uploadedData.url || uploadedData.filename,
            fileType: uploadedData.fileType || uploadedData.type || 'application/octet-stream',
            fileSize: uploadedData.fileSize || uploadedData.size || 0,
          }];
        }
      }

      await replyTicket({
        id: ticket?.id || id,
        message: replyMessage.trim(),
        attachments: uploadedReplyAttachments,
      }).unwrap();

      setReplyMessage('');
      setReplyFiles([]);
      refetch();
    } catch (err) {
      console.error('Error sending reply:', err);
    }
  };

  const handleMarkResolved = async () => {
    try {
      await updateTicketStatus({
        id: ticket?.id || id,
        status: 'resolved',
      }).unwrap();
      refetch();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    try { 
      await updateTicketStatus({
        id: ticket?.id || id,
        status: newStatus,
      }).unwrap();
      refetch();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  if (isLoading) {
    return (
      <MainContainer>
        <Box p={3}>
          <Skeleton variant="text" width={200} height={30} />
          <Skeleton variant="rectangular" height={200} sx={{ my: 2, borderRadius: '12px' }} />
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: '12px' }} />
        </Box>
      </MainContainer>
    );
  }

  if (isError || !ticket) {
    return (
      <MainContainer>
        <Box p={4} textAlign="center">
          <Typography variant="h6" color="error" mb={2}>
            Support Ticket Not Found
          </Typography>
          <CustomButton onClick={() => navigate('/support-tickets')}>
            Back to Tickets
          </CustomButton>
        </Box>
      </MainContainer>
    );
  }

  const isClosed = ticket.status?.toLowerCase() === 'closed';
  const isResolved = ticket.status?.toLowerCase() === 'resolved' || isClosed;
  const isSubmitDisabled = (!replyMessage.trim() && replyFiles.length === 0) || isReplying || isUploading;
  const statusBadge = getStatusBadge(ticket.status);
  const priorityBadge = getPriorityBadge(ticket.priority);

  return (
    <MainContainer>
      <Box className="support-tickets-card" sx={{ mb: 4 }}>
        {/* Top Header Navigation */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              className="back-rounded-icon d-flex align-items-center justify-content-center cursor-pointer"
              onClick={() => navigate('/support-tickets')}
              sx={{ border: '1px solid #4D4D4D', borderRadius: '50%', width: '30px', height: '30px', marginTop: '5px' }}
            >
              <ChevronLeftIcon />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '22px', color: '#111827', mt: 1 }}>
              {ticket.ticket_number || `TKT-${ticket.id}`} {ticket.subject}
            </Typography>
          </Box>

          {!isResolved && (
            <CustomButton
              onClick={handleMarkResolved}
              disabled={isUpdatingStatus}
              startIcon={<CheckCircleOutlineIcon />}
              sx={{
                borderRadius: '20px',
                backgroundColor: '#000000',
                color: '#ffffff',
                px: 3,
                py: 1,
                fontWeight: 600,
                fontSize: '14px',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#1F2937' },
              }}
            >
              Mark Resolved
            </CustomButton>
          )}
        </Box>

        {/* Main Grid Content */}
        <Grid container spacing={3}>
          {/* Left Column (2/3 width) */}
          <Grid item xs={12} md={8}>
            {/* Top Details Card */}
            <Box className="ticket-detail-box" mb={3}>
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mb={2.5}>
                {/* Primary Status Badge with Icon */}
                <span className={`ticket-pill ${statusBadge.className}`}>
                  {statusBadge.icon}
                  {statusBadge.label}
                </span>

                {/* Dynamic Awaiting Tag based on last reply */}
                {(ticket.status === 'open' || ticket.status === 'in_progress' || ticket.status === 'awaiting_vendor' || ticket.status === 'awaiting_admin') && (
                  <span className={`ticket-pill ${isLastFromAdmin ? 'status-awaiting_vendor' : 'status-awaiting_admin'}`}>
                    <AccessTimeIcon fontSize="inherit" />
                    {isLastFromAdmin ? 'Awaiting Vendor' : 'Awaiting Admin'}
                  </span>
                )}

                <span className={`ticket-pill ${priorityBadge.className}`}>
                  {priorityBadge.label}
                </span>
                {ticket.category && (
                  <span className="category-badge">🏷️ {ticket.category}</span>
                )}
                <span className="source-badge">Source: POS</span>
              </Box>

              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 0.5 }}>
                Description
              </Typography>
              <Typography variant="body1" sx={{ color: '#111827', fontSize: '15px', mb: 2 }}>
                {ticket.description}
              </Typography>

              {/* Showcase Last Message in Chat if available */}
              {lastMessage && (
                <Box sx={{ bgcolor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '10px', p: 2, mb: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#737373', display: 'block', mb: 0.5 }}>
                    Last Message ({lastMessage.sender_type === 'pos' || (!lastMessage.sender_type?.includes('admin') && lastMessage.sender_type !== 'super_admin') ? (lastMessage.sender?.full_name || ticket.creator?.full_name || 'Store User') : (lastMessage.sender?.full_name || 'Admin')} • {formatFullDate(lastMessage.created_at)})
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1F2937', fontWeight: 500 }}>
                    {lastMessage.message || (parseAttachments(lastMessage.attachments).length > 0 ? '📎 [Attachment(s)]' : '')}
                  </Typography>
                </Box>
              )}

              {/* Initial Ticket Attachments */}
              {ticketAttachments.length > 0 && (
                <Box mb={2}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#737373', display: 'block', mb: 0.5 }}>
                    Ticket Attachments
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {ticketAttachments.map((att, attIdx) => {
                      const fileName = getFileName(att, attIdx);
                      const fileUrl = getFileUrl(att);
                      return (
                        <Box
                          key={attIdx}
                          className="message-attachment-chip"
                          onClick={() => fileUrl && window.open(fileUrl, '_blank')}
                          sx={{ cursor: fileUrl ? 'pointer' : 'default' }}
                        >
                          <InsertDriveFileIcon fontSize="small" sx={{ color: '#4B5563', fontSize: 16 }} />
                          <Typography variant="caption" sx={{ fontWeight: 500, color: '#1F2937' }}>
                            {fileName}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* <Divider sx={{ my: 2 }} /> */}

              {/* <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" sx={{ color: '#737373', fontWeight: 500 }}>
                    Status:
                  </Typography>
                  <CustomSelect
                    value={ticket.status === 'awaiting_vendor' || ticket.status === 'awaiting_admin' ? 'in_progress' : (ticket.status || 'open')}
                    onChange={handleStatusChange}
                    options={[
                      { value: 'open', label: 'Open' },
                      { value: 'in_progress', label: 'In Progress' },
                      { value: 'resolved', label: 'Resolved' },
                      { value: 'closed', label: 'Closed' },
                    ]}
                    sx={{ minWidth: 140, height: 38 }}
                  />
                </Box>
              </Box> */}
            </Box>

            {/* Messages & Reply Card */}
            <Box className="ticket-detail-box">
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <ChatBubbleOutlineIcon sx={{ color: '#4B5563', fontSize: 20 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '16px', color: '#111827' }}>
                  Messages
                </Typography>
              </Box>

              {/* Messages Timeline (Filtered: Only conversations, including admin & POS replies) */}
              <Box display="flex" flexDirection="column" gap={2.5} mb={3}>
                {conversationMessages.length === 0 ? (
                  <Typography variant="body2" sx={{ color: '#737373', fontStyle: 'italic' }}>
                    No message conversations yet.
                  </Typography>
                ) : (
                  conversationMessages.map((msg, idx) => {
                    const isMySentMessage =
                      msg.sender_type === 'pos' ||
                      (!msg.sender_type?.includes('admin') && msg.sender_type !== 'super_admin');

                    const badgeText = isMySentMessage ? 'Sent' : 'Received';
                    const badgeClass = isMySentMessage ? 'sent' : 'received';
                    const senderName = isMySentMessage
                      ? (msg.sender?.full_name || ticket.creator?.full_name || ticket.creator?.first_name || 'Store User')
                      : (msg.sender?.full_name || msg.sender?.first_name || 'Admin');

                    const msgAttachments = parseAttachments(msg.attachments);

                    return (
                      <Box key={msg.id || idx} className="message-item-wrapper">
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <span className={`message-badge ${badgeClass}`}>
                            {badgeText}
                          </span>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151' }}>
                            {senderName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                            • {formatFullDate(msg.created_at)}
                          </Typography>
                        </Box>
                        {msg.message && (
                          <Typography variant="body2" sx={{ color: '#1F2937', fontSize: '14px', pl: 0.5 }}>
                            {msg.message}
                          </Typography>
                        )}

                        {/* Showcase message attachments if any */}
                        {msgAttachments.length > 0 && (
                          <Box display="flex" flexWrap="wrap" gap={1} mt={1} pl={0.5}>
                            {msgAttachments.map((att, attIdx) => {
                              const fileName = getFileName(att, attIdx);
                              const fileUrl = getFileUrl(att);
                              return (
                                <Box
                                  key={attIdx}
                                  className="message-attachment-chip"
                                  onClick={() => fileUrl && window.open(fileUrl, '_blank')}
                                  sx={{ cursor: fileUrl ? 'pointer' : 'default' }}
                                >
                                  <AttachFileIcon fontSize="small" sx={{ color: '#4B5563', fontSize: 16 }} />
                                  <Typography variant="caption" sx={{ fontWeight: 500, color: '#1F2937', lineHeight: 1 }}>
                                    {fileName}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    );
                  })
                )}
              </Box>

              {!isClosed && (
                <>
                  <Divider sx={{ my: 2.5 }} />

                  {/* Reply Section with Attachments Option */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                      Reply to system admin
                    </Typography>
                    <CustomTextArea
                      placeholder="Write your reply to system admin..."
                      minRows={4}
                      value={replyMessage}
                      disabled={isReplying || isUploading}
                      onChange={(e) => setReplyMessage(e.target.value.slice(0, 1000))}
                      style={{ background: 'transparent', borderRadius: '12px' }}
                    />

                    {/* Reply Attachments Selection & Staged List */}
                    <Box display="flex" alignItems="center" justifyContent="space-between" mt={1.5} flexWrap="wrap" gap={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          component="label"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            color: '#374151',
                            fontSize: '13px',
                            border: '1px solid #D1D5DB',
                            borderRadius: '8px',
                            px: 1.5,
                            py: 0.5,
                            bgcolor: '#F9FAFB',
                            '&:hover': { bgcolor: '#F3F4F6' },
                          }}
                        >
                          <AttachFileIcon fontSize="small" sx={{ fontSize: 16 }} />
                          <span>Attach file</span>
                          <input
                            type="file"
                            multiple
                            style={{ display: 'none' }}
                            onChange={handleReplyFileChange}
                            accept="image/*,video/*,.pdf,.doc,.docx,.zip"
                            disabled={isReplying || isUploading}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ color: '#9CA3AF', fontWeight: 500 }}>
                          {replyMessage.length}/1000
                        </Typography>
                      </Box>

                      <CustomButton
                        onClick={handleSendReply}
                        disabled={isSubmitDisabled}
                        startIcon={
                          isReplying || isUploading ? (
                            <CircularProgress size={16} sx={{ color: '#fff' }} />
                          ) : (
                            <SendIcon fontSize="small" />
                          )
                        }
                        sx={{
                          borderRadius: '10px',
                          backgroundColor: isSubmitDisabled ? '#E5E7EB !important' : '#000000 !important',
                          color: isSubmitDisabled ? '#9CA3AF !important' : '#ffffff !important',
                          px: 3,
                          py: 1,
                          fontWeight: 600,
                          fontSize: '14px',
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: isSubmitDisabled ? '#E5E7EB !important' : '#1F2937 !important',
                          },
                        }}
                      >
                        Send Reply
                      </CustomButton>
                    </Box>

                    {/* Staged Reply Files Preview List */}
                    {replyFiles.length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={1} mt={1.5}>
                        {replyFiles.map((file, idx) => (
                          <Box key={idx} className="attachment-chip">
                            <InsertDriveFileIcon fontSize="small" sx={{ color: '#4B5563', fontSize: 16 }} />
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                              {file.name}
                            </Typography>
                            <IconButton
                              size="small"
                              disabled={isReplying || isUploading}
                              onClick={() => handleRemoveReplyFile(idx)}
                              sx={{ color: '#EF4444', p: 0.2 }}
                            >
                              <DeleteIcon fontSize="small" sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Grid>

          {/* Right Column (1/3 width) */}
          <Grid item xs={12} md={4}>
            {/* Activity Timeline Card */}
            <Box className="ticket-detail-box">
              <Box display="flex" alignItems="center" gap={1} mb={2.5}>
                <AccessTimeIcon sx={{ color: '#4B5563', fontSize: 20 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '16px', color: '#111827' }}>
                  Activity timeline
                </Typography>
              </Box>

              <Box className="activity-timeline-list">
                {/* Initial Creation Event */}
                <Box className="activity-timeline-item">
                  <span className="timeline-dot" />
                  <Typography variant="caption" sx={{ color: '#737373', fontWeight: 600 }}>
                    {ticket?.creator?.full_name || 'Restaurant Admin'} • {formatFullDate(ticket.created_at)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#111827', fontSize: '13px', mt: 0.5 }}>
                    Ticket raised from POS — {ticket.category || 'General Issue'} issue.
                  </Typography>
                </Box>

                {/* Messages & Status Changes Events */}
                {messages.map((msg, idx) => (
                  <Box key={msg.id || idx} className="activity-timeline-item">
                    <span className="timeline-dot" />
                    <Typography variant="caption" sx={{ color: '#737373', fontWeight: 600 }}>
                      {msg?.sender?.full_name || (msg.sender_type === 'pos' ? (msg?.sender?.full_name || ticket?.creator?.full_name || 'Store User') : 'Admin')} • {formatFullDate(msg.created_at)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#111827', fontSize: '13px', mt: 0.5 }}>
                      {msg.message}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </MainContainer>
  );
};

export default TicketDetailScreen;
